import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { LiveAnnouncer } from "@angular/cdk/a11y";
import { UntypedFormControl } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Observable, Subscription, BehaviorSubject, of, from, Subject, combineLatest, concat } from "rxjs";
import { toArray, tap, switchMap, catchError, takeUntil, map } from "rxjs/operators";
import {
  Facet,
  Hit,
  DocSearchRequest,
  PubDocumentSearchResponse,
  PubFacetResponseType,
} from "src/app/_shared/models/publication-results.model";
import { PublicationService } from "src/app/dataset/selectContent/publication-refinecontent/publication-results/publication-refinement.service";
import { DatasetService } from "src/app/services/dataset/dataset.service";
import { TopSearch } from "src/app/shared/models/dataset.model";
import _m from "moment";
import { DocLimitModalComponent } from "src/app/shared/modal/doc-limit-modal/doc-limit-modal.component";
import { LOCAL_STORAGE_KEYS } from "src/app/shared/constants/storage-keys";

@Component({
    selector: "tdms-analysis-search",
    templateUrl: "./analysis-search.component.html",
    styleUrls: ["./analysis-search.component.scss"],
    standalone: false
})
export class AnalysisSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  searchField!: UntypedFormControl;
  error = "";
  clearFilter = false;
  isPublication = false;
  isShowFilters = false;
  searchStatusMessage = "";
  topSearches: TopSearch[] = [];
  topSearchLeft: TopSearch[] = [];
  topSearchRight: TopSearch[] = [];
  showTooltip = "";
  private searchStatusAnnouncementTimer = 0;
  private shouldFocusResultsCount = false;

  pubFacetSubscription: Subscription[] = [];
  pubFacetResults$?: Observable<{ type: string } | PubFacetResponseType | { type: string; err: unknown }>;

  @HostListener("keydown.escape")
  hideTooltip(): void {
    this.showTooltip = "";
  }

  @ViewChild("filterOverlay", { static: false }) set content(content: ElementRef) {
    if (content) {
      const height = content.nativeElement.offsetHeight;
      content.nativeElement.style.marginBottom = "-" + height + "px";
    }
  }

  @ViewChild("searchBar", { static: false }) searchBar!: ElementRef;

  //searchTermSubject and respective action stream...
  private searchTermSubject = new BehaviorSubject<string>("");
  searchTermAction$ = this.searchTermSubject.asObservable();

  //currently selected workbech observable.
  currentlySelectedWb$ = of(localStorage.getItem(LOCAL_STORAGE_KEYS.WORKBENCH) ?? "");

  //prepare observable for selected PubIds
  selectedPubIds$ = from(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.SELECTED_PUBS) ?? "[]")).pipe(
    map((val) => ({ searchValue: val })),
    toArray(),
    map((val) => JSON.stringify(val)),
    tap((val) => console.log("selectedPubIds => ", val))
  );

  displayFilters$ = this.pubservice.displayFilter$;

  pubOrderAsc$ = of("pubDateAsc");

  pubOrderDesc$ = of("pubDateDesc");

  cancelPubFacetRequest$ = new Subject<void>();

  searchRequestAsc$ = combineLatest([this.searchTermAction$, this.currentlySelectedWb$, this.pubOrderAsc$]).pipe(
    map(([searchTerm, wbId, sortOrderAsc]) => {
      //Trigger pubFacet request as soon as searchRequest is available.
      // const pubFacetReq = this.pubservice.generateSearchRequest(searchTerm, wbId, sortOrderAsc, 'PubFacet');
      // this.fetchPubFacets(pubFacetReq);
      return this.pubservice.generateAnalysisSearchRequest(searchTerm, wbId, sortOrderAsc);
    }),
    tap((val) => console.log("SQ =>", val))
  );

  //combine all the observables before search request.
  searchRequestDesc$ = combineLatest([this.searchTermAction$, this.currentlySelectedWb$, this.pubOrderDesc$]).pipe(
    map(([searchTerm, wbId, sortOrderDesc]) => {
      return this.pubservice.generateAnalysisSearchRequest(searchTerm, wbId, sortOrderDesc);
    }),
    tap((val) => console.log("SQ =>", val))
  );

  //Build resultsSteam based on searchRequest stream.
  docresultsDesc$ = this.searchRequestDesc$.pipe(
    tap((searchReq) => console.log("searchReq =>", JSON.stringify(searchReq))),
    switchMap((searchReq) =>
      concat(
        of({ type: "start" }),
        this.pubservice.performAnalysisSearch(searchReq).pipe(
          map((searchResp) => ({ type: "finish", searchResp })),
          catchError((err) => of({ type: "finish", err }))
        )
      )
    )
  );

  docresultsAsc$ = this.searchRequestAsc$.pipe(
    tap((searchReq) => console.log("searchReq =>", JSON.stringify(searchReq))),
    switchMap((searchReq) =>
      concat(
        of({ type: "start" }),
        this.pubservice.performAnalysisSearch(searchReq).pipe(
          map((searchResp) => ({ type: "finish", searchResp })),
          catchError((err) => of({ type: "finish", err }))
        )
      )
    )
  );

  //Following observable to collect the date range from Asc and Dec requests.
  //This is final observable subscribed from tml using async operator.
  dateRange$ = combineLatest([this.docresultsDesc$, this.docresultsAsc$]).pipe(
    tap(([descResultsObj, ascResultsObj]) => {
      if (
        descResultsObj.type === "finish" &&
        ascResultsObj.type === "finish" &&
        "searchResp" in descResultsObj &&
        descResultsObj.searchResp
      ) {
        this.announceSearchStatus(descResultsObj.searchResp.docsFound);
        if (this.shouldFocusResultsCount) {
          window.requestAnimationFrame(() => {
            if (this.shouldFocusResultsCount) {
              this.focusOnResultsCount()
              this.shouldFocusResultsCount = false;
            }
          });
        }
      }
    })
  );

  constructor(
    private pubservice: PublicationService,
    private dataSetService: DatasetService,
    private titleService: Title,
    private dialog: MatDialog,
    private router: Router,
    private liveAnnouncer: LiveAnnouncer
  ) {}

  ngOnInit() {
    this.titleService.setTitle("Create new dataset: Search");
    const currentOrDefaultSearchTerm = this.pubservice.currentSearchTerm() ?? "";
    this.searchField = new UntypedFormControl(currentOrDefaultSearchTerm);

    this.applyFilters(currentOrDefaultSearchTerm, true);
    this.isPublication = this.dataSetService.isPublication();
    this.getTopSearches();

    this.scrollToTop();
  }

  getTopSearches() {
    this.pubservice.getVizTopSearches().subscribe(
      (resp) => {
        this.topSearches = resp.popularDatabase ? resp.popularDatabase : [];

        /**
         * This is to address if workbench is not selected and popularSearch endpoint is returning empty obect.
         */
        if (!this.topSearches) return (this.topSearches = []);

        //parse date range out of title
        for (const search of this.topSearches) {
          if (search.name.includes("1")) {
            const name = search.name;
            search.name = search.name.slice(0, search.name.indexOf("1"));
            search.time = name.slice(name.indexOf("1"));
          }
        }
        //combine historic and currnert into one entry
        for (const search of this.topSearches) {
          if (this.topSearches.filter((x) => x.rank == search.rank).length > 1) {
            const searches = this.topSearches.filter((x) => x.rank == search.rank);
            const first = searches[0];
            const second = searches[1];
            if (!first || !second) {
              continue;
            }
            const isFirstCurrent = (first.time ?? "").localeCompare(second.time ?? "") > 0;
            const current = isFirstCurrent ? first : second;
            const historic = isFirstCurrent ? second : first;

            const combinedSearch: TopSearch = {
              moniker: current.moniker,
              name: current.name,
              historicMoniker: historic.moniker,
              description: current.description,
              full_description: current.full_description,
              rank: current.rank,
              time: current.time,
              historicTime: historic.time,
            };
            this.topSearches = this.topSearches.filter((x) => x.rank !== search.rank);
            this.topSearches.push(combinedSearch);
          }
        }

        this.topSearches.sort((a, b) => parseInt(a.rank) - parseInt(b.rank));
        this.topSearchLeft = this.topSearches.slice(0, Math.ceil(this.topSearches.length / 2));
        this.topSearchRight = this.topSearches.slice(Math.ceil(this.topSearches.length / 2));
      },
      (error) => {
        console.log("Tops Search Error ---> ", error);
        this.topSearches = [];
      }
    );
  }

  showTopSearch(obsRes: Array<{ type: string }>): boolean {
    if (!this.topSearches) return false;
    const searchReq = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.S_REQ) ?? '{"search":{"filters":[],"products":[],"query":""}}');

    return (
      this.topSearches.length > 0 &&
      obsRes[0].type === "finish" &&
      obsRes[1].type === "finish" &&
      !((searchReq.search?.filters?.length ?? 0) > 0 || (searchReq.search?.products?.length ?? 0) > 0) &&
      (searchReq.search?.query ==
        '"artificial intelligence" or "text mining" or "machine learning" or "data science"' ||
        searchReq.search?.query == "")
    );
  }

  public search(searchValue: string) {
    this.searchField.setValue(searchValue);
    localStorage.setItem(LOCAL_STORAGE_KEYS.SEARCH_VALUE, searchValue);
    this.cancelPubFacetRequest$.next();
    this.searchTermSubject.next(searchValue);
  }

  clear() {
    this.searchField.reset();
    this.searchField.setValue("");
    localStorage.setItem(LOCAL_STORAGE_KEYS.SEARCH_VALUE, "");
    this.setSearchBarFocus();
  }

  isDocs(docsFound: number): boolean {
    return docsFound > 0;
  }

  pubFacets(facets: Facet[]) {
    if (facets) {
      const pubfacets = this.pubservice.getPubFacets(facets);
      localStorage.setItem(LOCAL_STORAGE_KEYS.PUB_FACETS, JSON.stringify(pubfacets));
      return pubfacets;
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEYS.PUB_FACETS, JSON.stringify(this.getFacetsfromSearchReq("pubTitle")));
    }
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.PUB_FACETS) ?? "[]");
  }

  sourceFacets(facets: Facet[]) {
    if (facets) {
      return this.pubservice.getSourceFacets(facets);
    } else {
      return this.getFacetsfromSearchReq("sourcetype");
    }
  }

  docFacets(facets: Facet[]) {
    if (facets) {
      return this.pubservice.getDocTypeFacets(facets);
    } else {
      return this.getFacetsfromSearchReq("objectype");
    }
  }

  dbFacets(facets: Facet[]) {
    const dbFacets = facets ? this.pubservice.getDbFacets(facets) : this.getFacetsfromSearchReq("databases");
    localStorage.setItem(LOCAL_STORAGE_KEYS.DB_FACETS, JSON.stringify(dbFacets));
    return dbFacets;
  }

  getFacetsfromSearchReq(facetType: string) {
    const facets: Array<{ searchValue: string; displayValue: string; count: number }> = [];
    const searchReq = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.S_REQ) ?? '{"search":{"filters":[]}}');
    const matchingFilter = searchReq.search?.filters?.find((filter: { name: string }) => filter.name == facetType);

    if (matchingFilter?.entries) {
      for (const facet of matchingFilter.entries) {
        facets.push({
          searchValue: facet.searchValue,
          displayValue: facet.searchValue,
          count: 0,
        });
      }
    }
    return facets;
  }

  applyTopSearch(moniker: string, name: string) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.DB_FILTERS, JSON.stringify([{ moniker: moniker, name: name }]));
    this.clear();
    this.applyFilters("");
  }

  applyFilters(searchValue?: string, skipFocus:boolean = false) {
    if (searchValue != null) {
      this.searchTermSubject.next(searchValue);
    } else {
      this.searchTermSubject.next(localStorage.getItem(LOCAL_STORAGE_KEYS.SEARCH_VALUE) ?? "");
    }

    this.pubservice.dbProduct$.subscribe((val) => console.log(val));

    this.isShowFilters = false;

    this.scrollToTop();

    this.shouldFocusResultsCount = !skipFocus;
  }

  focusOnResultsCount(): boolean {
    const resultsDocCountElement = Array.from(document.querySelectorAll<HTMLElement>('#results-doc-count')).find(
      (element) => element.getClientRects().length > 0
    );

    if (resultsDocCountElement) {
      resultsDocCountElement.tabIndex = 0;
      resultsDocCountElement.focus();
      return document.activeElement === resultsDocCountElement;
    }

    return false;
  }

  removeResultsDocTabIndex(event: FocusEvent): void {
    (event.currentTarget as HTMLElement).removeAttribute("tabindex");
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  public displayClearFilters(): boolean | false {
    return this.pubservice.displayClearFilters();
  }

  getFooterHeight(numOfDocs: number): number {
    if (numOfDocs < 100000) {
      return 92;
    }

    const extraDigits = numOfDocs.toString().length - 5;
    const commas = Math.ceil(numOfDocs.toString().length / 3) - 1;

    return 72 + extraDigits * 9 + commas * 5;
  }

  onToggleShowFilters() {
    this.isShowFilters = !this.isShowFilters;
  }
  formatDate(date: string): string {
    return _m(new Date(date)).utcOffset(0, false).format("MMM DD, YYYY");
  }

  isAppliedFilters() {
    return (
      localStorage.getItem(LOCAL_STORAGE_KEYS.DOC_FILTERS) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_FILTERS) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.FROM_DATE) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.TO_DATE)
    );
  }

  displayDateRange(results: Hit[], resultsAsc: Hit[]) {
    const dateDesc = results[0]?.field.find((element) => element.name === "numStartDate")?.value;
    const dateAsc = resultsAsc[0]?.field.find((element) => element.name === "numStartDate")?.value;
    const dateDescValue = Array.isArray(dateDesc) ? dateDesc[0] : undefined;
    const dateAscValue = Array.isArray(dateAsc) ? dateAsc[0] : undefined;

    if (!dateAscValue || !dateDescValue) {
      return "";
    }

    return this.formatDate(dateAscValue) + " to " + this.formatDate(dateDescValue);
  }

  createDataset(docCount: number) {
    const maxDocCount = 10000;
    const dialogData = { docCount: maxDocCount };

    if (docCount > maxDocCount) {
      const dialogRef: MatDialogRef<DocLimitModalComponent> = this.dialog.open(DocLimitModalComponent, {
        disableClose: true,
        // maxWidth: "620px",
        data: dialogData,
      });
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEYS.DOC_COUNT, docCount.toString());
      this.router.navigateByUrl("/analysis/createdataset");
    }
  }

  showInfo(isHover: boolean, index: string) {
    if (isHover) {
      this.showTooltip = index;
    } else {
      this.showTooltip = "";
    }
  }

  private announceSearchStatus(docsFound: number) {
    const count = docsFound ?? 0;
    const formattedCount = count.toLocaleString("en-US");
    let announcement: string;
    if (count === 0) {
      announcement = "No documents found";
    } else if (count === 1) {
      announcement = "1 document found";
    } else {
      announcement = `${formattedCount} documents found`;
    }

    this.searchStatusMessage = "";
    this.liveAnnouncer.clear();

    if (this.searchStatusAnnouncementTimer) {
      window.clearTimeout(this.searchStatusAnnouncementTimer);
    }

    this.searchStatusAnnouncementTimer = window.setTimeout(() => {
      this.searchStatusMessage = announcement;
      this.liveAnnouncer.announce(announcement, "polite");
    }, 100);
  }

  private fetchPubFacets(pubFacetSearchReq: DocSearchRequest) {
    console.log("Fetching pubfacets....", JSON.stringify(pubFacetSearchReq));

    this.pubFacetResults$ = of(pubFacetSearchReq).pipe(
      takeUntil(this.cancelPubFacetRequest$),
      switchMap((searchReq) =>
        concat(
          of({ type: "start" }),
          this.pubservice.search(searchReq).pipe(
            map((searchResp: PubDocumentSearchResponse) => {
              const pubFacetResp: PubFacetResponseType = {
                type: "finish",
                searchResp,
              };
              console.log("pubFacets call completed...", pubFacetResp.searchResp?.facets?.length ?? 0);
              return pubFacetResp;
            }),
            catchError((err) => of({ type: "finish", err }))
          )
        )
      )
    );
  }

  ngAfterViewInit(): void {
    console.log("wb-refinement  ngAfterViewInit method....");
    this.setSearchBarFocus();
  }

  setSearchBarFocus() {
    this.searchBar.nativeElement.focus();
  }

  /**
   * This method is used to inform filter component to render lazyResponse.
   * This is temporary commented out as we are not using this approach.
   
    onFilterComponentRendered() {
      console.log('wb-refinemnt onFilterComponentRendered event...');
      this.pubFacetResults$.subscribe((res: PubFacetResponseType) => {

          if(res.type === 'finish') {
              console.log('Informing filter component to render lazyResponse @@@@ ', res.type);
              this.pubservice.setLazyPubFacetSubjectV1(res);
          }
          
        });      
      
    }
  */

  ngOnDestroy(): void {
    if (this.searchStatusAnnouncementTimer) {
      window.clearTimeout(this.searchStatusAnnouncementTimer);
    }

    console.log("ngOnDestroy method...");
    localStorage.removeItem(LOCAL_STORAGE_KEYS.DB_LIST_VIS);
  }

  onJumpToResults() {
    this.isShowFilters = false;
  }
}
